<template>
  <div>
    <v-flex d-flex>
      <v-subheader class="font-weight-bold">
        <span id="users__rows">
          <span id="users__total-string">Total: </span>
          <span id="users__rows-count">{{ rowsCount }}</span>
        </span>
      </v-subheader>
      <v-spacer></v-spacer>
      <v-text-field
        v-model="search"
        :label="$t('actions.search')"
        class="mx-2"
        prepend-icon="mdi-magnify"
        single-line
        clearable
      ></v-text-field>
    </v-flex>
    <v-data-table
      ref="dataContainer"
      :headers="headers"
      :items="loadItems"
      class="elevation-1 mx-2 scroll"
      :loading="loading"
      hide-default-footer
      :options.sync="options"
      style="min-height: 100%"
      id="users__table"
      must-sort
      fixed-header
      :height="height - 15 + 'px'"
    >
      <template v-slot:body.append>
        <td
          colspan="3"
          class="text-center py-2"
          v-if="options.itemsPerPage < loadItems.length"
        >
          <v-progress-circular
            :size="30"
            color="primary"
            indeterminate
          ></v-progress-circular>
        </td>
      </template>
      <template v-slot:item.action="props">
        <v-tooltip
          :top="props.item.environment_manager ? true : false"
          :bottom="!props.item.environment_manager ? true : false"
          :color="props.item.environment_manager ? 'warning' : null"
        >
          <template v-slot:activator="{ on }">
            <span v-on="on">
              <v-icon
                small
                class="mr-6"
                @click="editItem(props.item)"
                :id="'users__edit-' + props.index"
                :disabled="
                  !isPermitted(PermissionEnum.USERS.UPDATE) ||
                    props.item.environment_manager
                "
                >mdi-pencil</v-icon
              >
            </span>
          </template>
          <span>{{
            props.item.environment_manager
              ? $t("validation.restrictEditingEnvironmentManager")
              : $t("actions.edit")
          }}</span>
        </v-tooltip>
        <v-tooltip
          :top="props.item.environment_manager ? true : false"
          :bottom="!props.item.environment_manager ? true : false"
          :color="props.item.environment_manager ? 'warning' : null"
        >
          <template v-slot:activator="{ on }">
            <span v-on="on">
              <v-icon
                small
                @click="deleteItem(props.item)"
                :id="'users__delete-' + props.index"
                :disabled="
                  !isPermitted(PermissionEnum.USERS.DELETE) ||
                    props.item.environment_manager ||
                    props.item.username === loggedUserName
                "
                >mdi-delete</v-icon
              >
            </span>
          </template>
          <span>{{
            props.item.environment_manager
              ? $t("validation.restrictDeletingEnvironmentManager")
              : $t("actions.delete")
          }}</span>
        </v-tooltip>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { handleError } from "@/utils/error";
import { permissionMixin } from "@/mixins/permissions";

export default {
  name: "UsersTable",
  mixins: [permissionMixin],
  data() {
    return {
      height: 0,
      rows: 20,
      loadItems: [],
      options: {},
      loading: false,
      headers: [
        {
          text: this.$t("modules.users.username"),
          align: "left",
          value: "username"
        },
        {
          text: this.$t("modules.users.description"),
          align: "left",
          value: "description"
        },
        {
          text: this.$t("actions.actions"),
          align: "center",
          value: "action",
          sortable: false
        }
      ],
      editedIndex: -1
    };
  },

  computed: {
    dialog: {
      get() {
        return this.$store.state.users.dialog;
      },
      set(val) {
        this.$store.dispatch("users/toggleDialog", val);
      }
    },
    search: {
      get() {
        return this.$store.state.users.search || "";
      },
      set(val) {
        this.$store.commit("users/updateSearch", val);
      }
    },
    users() {
      return this.$store.state.users.users;
    },
    editedItem() {
      return this.$store.state.users.editedItem;
    },
    rowsCount() {
      return this.loadItems.length;
    },
    loggedUserName() {
      return this.$store.state.login.user.name;
    }
  },

  created() {
    this.initialize();
  },
  beforeDestroy() {
    document
      .querySelector(".v-data-table__wrapper")
      .removeEventListener("scroll", this.scroll);
  },
  mounted() {
    let el = document.querySelector(".v-data-table__wrapper");
    el.addEventListener("scroll", this.scroll);
    this.computeHeight();
  },

  watch: {
    search(val) {
      this.searchItems(val);
    },
    users() {
      if (!this.search) {
        this.loadItems = this.users;
      } else {
        this.searchItems(this.search);
      }
    }
  },

  methods: {
    scroll({ target: { scrollTop, clientHeight, scrollHeight } }) {
      if (this.options.itemsPerPage < this.users.length) {
        if (scrollTop + clientHeight >= scrollHeight - 5) {
          this.paginate();
        }
      }
    },
    computeHeight() {
      let el = this.$refs.dataContainer.$el;
      let boxOffset = el.getBoundingClientRect();
      let viewPortHeight = window.innerHeight;
      this.height = viewPortHeight - boxOffset.top;
    },
    paginate() {
      this.options.itemsPerPage = this.options.itemsPerPage + this.rows;
    },
    initialize() {
      this.options.itemsPerPage = this.rows;
      this.loading = true;
      this.$store
        .dispatch("users/fetchAll")
        .then(() => {
          if (this.search) {
            this.searchItems(this.search);
          } else {
            this.loadItems = this.users;
          }
        })
        .catch(error => {
          handleError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    searchItems(val) {
      this.loadItems = this.users.filter(o =>
        this.headers
          .filter(item => item.value !== "action")
          .map(item => item.value)
          .some(
            k =>
              o[k]
                .toString()
                .toLowerCase()
                .indexOf(val.toLowerCase()) > -1
          )
      );
    },

    editItem(item) {
      let editedIndex = this.users.indexOf(item);
      this.$store.commit("users/updateFormTitle", 2);
      this.$store.commit("users/updateIndex", editedIndex);
      item.password = "";
      item.email = item.email || "";
      this.$store.dispatch("users/editItem", item);
      this.dialog = true;
    },

    deleteItem(item) {
      this.loading = true;
      let confirmed = confirm(this.$t("modules.users.confirmDelete"));
      if (confirmed) {
        this.$store
          .dispatch("users/delete", item.id)
          .catch(error => {
            handleError(error);
          })
          .finally(() => {
            this.loading = false;
          });
      } else {
        this.loading = false;
      }
    }
  }
};
</script>
